import '@src/theme/styles/globals.css';
import '@src/theme/styles/fonts';
import { ApolloClient } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { ScoChakraProvider } from '@src/components/chakra-ui/provider';
import { OnboardingHighlightProvider } from '@src/components/sco/Requests/RequestOnboarding/OnboardingHighlightContext.tsx';
import { RouteErrorBoundary } from '@src/components/sco/utilities/AppErrorBoundary';
import { BreakpointContextProvider } from '@src/components/sco/utilities/BreakpointProvider';
import { BreakpointCookieTracker } from '@src/components/sco/utilities/BreakpointProvider/BreakpointCookieTracker';
import { CurrentUserProvider } from '@src/components/sco/utilities/CurrentUserProvider';
import { DevEnvAuthGate } from '@src/components/sco/utilities/DevEnvAuthGate';
import { HubspotChatHandler } from '@src/components/sco/utilities/HubspotChatHandler';
import { PromptHandler } from '@src/components/sco/utilities/PromptHandler';
import { SEOProvider } from '@src/components/sco/utilities/SEOProvider';
import { ActiveContentContextProvider } from '@src/contexts/ActiveContentContext';
import { AppContextStoreProvider } from '@src/contexts/AppContextStore';
import { ContextStore, createContextStore, getClientSideContextStore } from '@src/contexts/AppContextStore/context-store';
import { useWindow } from '@src/hooks/useWindow';
import withCustomSSR from '@src/lib/CustomSSR';
import { createApolloClient, getClientSideApolloClient } from '@src/lib/apolloClient';
import { createEmotionCache } from '@src/lib/createEmotionCache';
import { getWidthFromUserAgent } from '@src/lib/getWidthFromUserAgent';
import { IS_SSR } from '@src/lib/server-side-rendering';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { usePathname, useSearchParams } from 'next/navigation';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import React, { JSX, useEffect, useMemo } from 'react';
type Startups5AppProps = {
  Component?: any;
  apolloClient?: ApolloClient;
  apolloState?: any;
  contextState?: any;
  contextStore?: ContextStore;
  contextStoreState?: any;
  emotionCache?: EmotionCache;
  initialWindowWidth?: number;
  pageProps?: any;
  renderPass?: string;
  scoDevPin?: string;
  userAgent?: any;
};
const pageTrackingHistory = [];
type Startups5AppType = JSX.ElementType & {
  getServerSideProps?: any;
};
let Startups5App: Startups5AppType = (props: Startups5AppProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const apolloClient = props.apolloClient || getClientSideApolloClient(props.apolloState);
  const renderTime = useMemo(() => new Date(), []);
  const window = useWindow({
    initialWidth: props.initialWindowWidth,
    userAgent: props.userAgent
  });
  const {
    Component
  } = props;
  if (parseInt(process.env.NEXT_PUBLIC_SSR_LOGGING) > 0) {
    console.log({
      renderTime: renderTime,
      renderPass: props.renderPass || (IS_SSR ? 'NEXTJS' : 'BROWSER'),
      userAgent: props.userAgent,
      initialWindowWidth: props.initialWindowWidth,
      contextState: props.contextState,
      apolloState: props.apolloState
    });
  }
  useEffect(() => {
    try {
      const pageTrackingKey = `${pathname}|${searchParams.toString()}`;
      if (typeof window !== 'undefined' && window.analytics && pageTrackingHistory[pageTrackingHistory.length - 1] != pageTrackingKey) {
        pageTrackingHistory.push(pageTrackingKey);
        console.log(`Track Page: ${pageTrackingKey}`);
        window.analytics.page();
      }
    } catch (err) {
      console.log(err);
    }
  }, [pathname, searchParams.toString()]);
  const getLayout = Component.getLayout || (page => page);
  const emotionCache = useMemo(() => props.emotionCache || createEmotionCache(), [props.emotionCache]);
  return <CacheProvider value={emotionCache}>
      <AppContextStoreProvider contextStore={props.contextStore || getClientSideContextStore(props.contextState)}>
        <NuqsAdapter>
          <ApolloProvider client={apolloClient}>
            <ScoChakraProvider>
              <BreakpointContextProvider initialWidth={props.initialWindowWidth} userAgent={props.userAgent} userAgentWidth={getWidthFromUserAgent(props.userAgent)}>
                <DevEnvAuthGate scoDevPin={props.scoDevPin}>
                  <CurrentUserProvider>
                    <ActiveContentContextProvider>
                      <OnboardingHighlightProvider>
                        {getLayout(<RouteErrorBoundary>
                            <Component {...{
                          pageProps: props.pageProps
                        }} />
                          </RouteErrorBoundary>)}
                        <SEOProvider />
                      </OnboardingHighlightProvider>
                    </ActiveContentContextProvider>
                  </CurrentUserProvider>
                  <PromptHandler />
                  <HubspotChatHandler />
                  <Analytics beforeSend={data => {
                  if (data.url.includes('/admin/')) {
                    return null; // this will ignore the event
                  } else {
                    return data;
                  }
                }} />
                  <SpeedInsights beforeSend={data_0 => {
                  if (data_0.url.includes('/admin/')) {
                    return null; // this will ignore the event
                  } else {
                    return data_0;
                  }
                }} />
                </DevEnvAuthGate>
              </BreakpointContextProvider>
            </ScoChakraProvider>
          </ApolloProvider>
          <BreakpointCookieTracker />
        </NuqsAdapter>
      </AppContextStoreProvider>
    </CacheProvider>;
};
if (IS_SSR) {
  Startups5App.getServerSideProps = async appContext => {
    return {
      props: {}
    };
  };
  Startups5App = withCustomSSR({
    getApolloClient: ({
      headers,
      apolloClient,
      apolloState
    }) => {
      return apolloClient || createApolloClient({
        headers,
        apolloState
      });
    },
    getContextStore: ({
      contextStore,
      contextState
    }) => {
      return contextStore || createContextStore({
        contextState
      });
    },
    getApolloDataFromTree: getDataFromTree
    // getApolloDataFromTree: async tree => await prerenderStatic({ tree, renderFunction: renderToString }),
  })(Startups5App);
}
export default Startups5App;